import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { FiCheck, FiChevronDown, FiEdit2, FiPlus, FiTrash } from "react-icons/fi";

const SLOT_WIDTH = "w-full";
const TIME_WIDTH = "flex-1 min-w-0";

const deleteButtonClass = (canDelete, dayOff) =>
    `flex h-7 w-7 shrink-0 items-center justify-center rounded text-nt-red ${
        canDelete && !dayOff
            ? "hover:bg-red-100"
            : "cursor-no-drop opacity-30"
    } disabled:cursor-no-drop disabled:opacity-30`;

const TimeSelect = ({ name, options, disabled, control, rules, triggerClassName = "" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return undefined;

        const handleClickOutside = (event) => {
            if (!containerRef.current?.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            render={({ field: { value, onChange }, fieldState }) => {
                const selected = options.find((o) => o.value === value || o.value === Number(value));
                const hasError = Boolean(fieldState.error);

                return (
                    <div ref={containerRef} className={`relative ${TIME_WIDTH}`}>
                        {hasError ? (
                            <span
                                className="absolute -top-1.5 -right-1.5 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-nt-red text-[10px] font-bold leading-none text-white pointer-events-none"
                                aria-hidden="true"
                            >
                                !
                            </span>
                        ) : null}
                        <button
                            type="button"
                            disabled={disabled}
                            aria-invalid={hasError}
                            onClick={() => setIsOpen((open) => !open)}
                            className={`flex h-8 w-full items-center justify-center gap-1 rounded bg-white px-2 text-sm text-nt-black outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-40 ${
                                hasError ? "border-2 border-nt-red" : "border border-nt-grey-light"
                            } ${triggerClassName}`}
                        >
                            <span className="tabular-nums truncate text-center">{selected?.label ?? "--"}</span>
                            <FiChevronDown
                                size={12}
                                className={`shrink-0 text-nt-grey transition-transform ${isOpen ? "rotate-180" : ""}`}
                            />
                        </button>

                        {isOpen && !disabled ? (
                            <ul
                                className="absolute left-0 top-full z-50 mt-1 max-h-48 w-full overflow-y-auto rounded border border-nt-grey-light bg-white py-1 shadow-md"
                                role="listbox"
                            >
                                {options.map((o) => {
                                    const isSelected = o.value === value || o.value === Number(value);

                                    return (
                                        <li key={o.value} role="option" aria-selected={isSelected}>
                                            <button
                                                type="button"
                                                className={`w-full px-2 py-1.5 text-left text-sm tabular-nums outline-none focus:outline-none hover:bg-nt-grey-light/60 ${
                                                    isSelected ? "bg-nt-grey-light/80 font-medium text-nt-blue" : "text-nt-black"
                                                }`}
                                                onClick={() => {
                                                    onChange(o.value);
                                                    setIsOpen(false);
                                                }}
                                            >
                                                {o.label}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : null}
                    </div>
                );
            }}
        />
    );
};

const includeSelectedOption = (filtered, allOptions, currentValue) => {
    if (currentValue === null || currentValue === undefined || currentValue === "") {
        return filtered;
    }
    const numericValue = Number(currentValue);
    if (filtered.some((option) => option.value === numericValue)) {
        return filtered;
    }
    const selected = allOptions.find((option) => option.value === numericValue);
    return selected ? [...filtered, selected].sort((a, b) => a.value - b.value) : filtered;
};

const formatTimeLabel = (value, options) => {
    const option = options.find((o) => o.value === value || o.value === Number(value));
    return option?.label ?? "--";
};

const SliceBlock = ({
    dayId,
    index,
    dayOff,
    isEditing,
    isValid,
    hasError,
    canDelete,
    startOptions,
    endOptions,
    control,
    getValues,
    options,
    onConfirm,
    onEdit,
    onDelete,
}) => {
    const startRules = dayOff ? undefined : {
        required: "Start time is required",
        validate: (value) => {
            if (value === null || value === undefined || value === "") {
                return "Start time is required";
            }
            if (index > 0) {
                const prevEnd = getValues(`days.${dayId}.slices.${index - 1}.end`);
                if (typeof prevEnd === "number" && value < prevEnd) {
                    return "Time slot must start after the previous slot ends";
                }
            }
            return true;
        },
    };

    const endRules = dayOff ? undefined : {
        required: "End time is required",
        validate: (value) => {
            if (value === null || value === undefined || value === "") {
                return "End time is required";
            }
            const start = getValues(`days.${dayId}.slices.${index}.start`);
            if (typeof start === "number" && value <= start) {
                return "End time must be after start time";
            }
            return true;
        },
    };

    if (isEditing) {
        const editingContainerClass = hasError
            ? "border-2 border-nt-red bg-red-50/30"
            : "border-2 border-nt-blue bg-blue-50/40";

        return (
            <div className={`flex h-9 items-center gap-1.5 rounded-md px-1.5 ${SLOT_WIDTH} ${editingContainerClass}`}>
                <TimeSelect
                    name={`days.${dayId}.slices.${index}.start`}
                    options={startOptions}
                    disabled={dayOff}
                    control={control}
                    rules={startRules}
                />
                <span className="text-nt-grey text-xs shrink-0">–</span>
                <TimeSelect
                    name={`days.${dayId}.slices.${index}.end`}
                    options={endOptions}
                    disabled={dayOff}
                    control={control}
                    rules={endRules}
                />
                <div className="flex w-14 shrink-0 items-center justify-center">
                    <button
                        type="button"
                        disabled={dayOff}
                        onClick={() => {
                            if (!isValid) return;
                            onConfirm();
                        }}
                        aria-disabled={!isValid || dayOff}
                        aria-label="Confirm time slice"
                        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded bg-transparent transition disabled:bg-transparent disabled:cursor-not-allowed ${
                            isValid && !dayOff
                                ? "cursor-pointer text-emerald-600 hover:bg-emerald-100"
                                : "cursor-not-allowed text-nt-grey"
                        }`}
                    >
                        <FiCheck
                            size={16}
                            strokeWidth={2.5}
                            className={`pointer-events-none ${isValid && !dayOff ? "text-emerald-600" : "text-nt-grey"}`}
                        />
                    </button>
                    <button
                        type="button"
                        disabled={dayOff || !canDelete}
                        onClick={onDelete}
                        aria-label="Delete time slice"
                        className={deleteButtonClass(canDelete, dayOff)}
                    >
                        <FiTrash size={14} />
                    </button>
                </div>
            </div>
        );
    }

    const start = getValues(`days.${dayId}.slices.${index}.start`);
    const end = getValues(`days.${dayId}.slices.${index}.end`);

    return (
        <div className={`flex h-9 items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50/60 px-2 ${SLOT_WIDTH}`}>
            <span className="flex-1 text-center text-sm tabular-nums text-nt-black">
                {formatTimeLabel(start, options)}
            </span>
            <span className="text-nt-grey text-xs shrink-0">–</span>
            <span className="flex-1 text-center text-sm tabular-nums text-nt-black">
                {formatTimeLabel(end, options)}
            </span>
            <div className="flex w-14 shrink-0 items-center justify-center">
                <button
                    type="button"
                    disabled={dayOff}
                    onClick={onEdit}
                    aria-label="Edit time slice"
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-nt-blue hover:bg-blue-100 disabled:opacity-40"
                >
                    <FiEdit2 size={14} />
                </button>
                <button
                    type="button"
                    disabled={dayOff || !canDelete}
                    onClick={onDelete}
                    aria-label="Delete time slice"
                    className={deleteButtonClass(canDelete, dayOff)}
                >
                    <FiTrash size={14} />
                </button>
            </div>
        </div>
    );
};

const SingleDaySlices = ({ id, name, options }) => {
    const {
        formState: { errors, submitCount },
        watch,
        control,
        getValues,
        setValue,
    } = useFormContext();

    const [editingSlices, setEditingSlices] = useState(() => new Set());

    const dayOff = watch(`days.${id}.off`, false);
    const shortName = name?.length > 3 ? name.slice(0, 3) : name;

    useEffect(() => {
        if (!dayOff) {
            const slices = getValues(`days.${id}.slices`);
            if (!slices?.length) {
                setValue(`days.${id}.slices`, [{ start: 8, end: 18 }]);
            }
        }
    }, [dayOff, id, getValues, setValue]);

    const {
        fields: sliceList,
        append: addSlice,
        remove: removeSlice,
    } = useFieldArray({
        control,
        name: `days.${id}.slices`,
    });

    const isSliceValid = useCallback((index) => {
        const start = getValues(`days.${id}.slices.${index}.start`);
        const end = getValues(`days.${id}.slices.${index}.end`);

        if (start === null || start === undefined || start === "") return false;
        if (end === null || end === undefined || end === "") return false;
        if (typeof start !== "number" || typeof end !== "number" || end <= start) return false;

        if (index > 0) {
            const prevEnd = getValues(`days.${id}.slices.${index - 1}.end`);
            if (typeof prevEnd === "number" && start < prevEnd) return false;
        }

        return true;
    }, [getValues, id]);

    const sliceHasError = useCallback((index) => {
        return Boolean(
            errors?.days?.[id]?.slices?.[index]?.start ||
            errors?.days?.[id]?.slices?.[index]?.end,
        );
    }, [errors, id]);

    useEffect(() => {
        setEditingSlices((prev) => {
            const next = new Set(prev);
            sliceList.forEach((_, index) => {
                if (!isSliceValid(index)) {
                    next.add(index);
                }
            });
            return next;
        });
    }, [sliceList.length, isSliceValid]);

    useEffect(() => {
        if (!submitCount) return;

        setEditingSlices((prev) => {
            const next = new Set(prev);
            sliceList.forEach((_, index) => {
                const hasFieldError = Boolean(
                    errors?.days?.[id]?.slices?.[index]?.start ||
                    errors?.days?.[id]?.slices?.[index]?.end,
                );
                if (hasFieldError || !isSliceValid(index)) {
                    next.add(index);
                }
            });
            return next;
        });
    }, [submitCount, errors, id, sliceList, isSliceValid]);

    const lastSliceEnd = watch(`days.${id}.slices.${sliceList.length - 1}.end`);
    const hasEditingSlice = editingSlices.size > 0;
    const lastSliceValid = sliceList.length > 0 && isSliceValid(sliceList.length - 1);
    const canAddSlice = lastSliceEnd < 23 && !dayOff && !hasEditingSlice && lastSliceValid;

    const confirmSlice = (index) => {
        if (!isSliceValid(index)) return;
        setEditingSlices((prev) => {
            const next = new Set(prev);
            next.delete(index);
            return next;
        });
    };

    const editSlice = (index) => {
        setEditingSlices((prev) => new Set(prev).add(index));
    };

    const handleRemoveSlice = (index) => {
        removeSlice(index);
        setEditingSlices((prev) => {
            const next = new Set();
            prev.forEach((i) => {
                if (i < index) next.add(i);
                else if (i > index) next.add(i - 1);
            });
            return next;
        });
    };

    const handleAddSlice = () => {
        const newIndex = sliceList.length;
        addSlice({});
        setEditingSlices((prev) => new Set(prev).add(newIndex));
    };

    const dayErrorMessage = useMemo(() => {
        const messages = [];
        sliceList.forEach((_, index) => {
            if (!editingSlices.has(index)) return;
            const startMsg = errors?.days?.[id]?.slices?.[index]?.start?.message;
            const endMsg = errors?.days?.[id]?.slices?.[index]?.end?.message;
            if (startMsg) messages.push(startMsg);
            if (endMsg) messages.push(endMsg);
        });
        return [...new Set(messages)][0] ?? null;
    }, [errors, id, sliceList, editingSlices]);

    return (
        <div className={`py-1.5 border-b border-nt-grey ${dayOff ? "opacity-50" : ""}`}>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2 min-h-[40px]">
                <p className="w-full sm:w-10 shrink-0 text-xs font-semibold uppercase tracking-wide text-nt-grey">
                    {shortName}
                </p>

                <div className="flex flex-1 flex-col items-stretch gap-2 min-w-0">
                    {sliceList.map((slice, index) => {
                        const sliceStart = watch(`days.${id}.slices.${index}.start`);
                        const sliceEnd = getValues(`days.${id}.slices.${index - 1}.end`);
                        const currentStart = getValues(`days.${id}.slices.${index}.start`);
                        const currentEnd = getValues(`days.${id}.slices.${index}.end`);
                        const startOptions = includeSelectedOption(
                            index > 0
                                ? options.filter((v) => v.value > sliceEnd && v.value < 24)
                                : options.filter((v) => v.value < 24),
                            options,
                            currentStart,
                        );
                        const endOptions = includeSelectedOption(
                            sliceStart
                                ? options.filter((v) => v.value > sliceStart)
                                : sliceEnd
                                    ? options.filter((v) => v.value > sliceEnd)
                                    : options,
                            options,
                            currentEnd,
                        );

                        const isEditing = editingSlices.has(index);
                        const valid = isSliceValid(index);
                        const hasError = sliceHasError(index) || (isEditing && !valid && (currentStart !== "" && currentStart != null || currentEnd !== "" && currentEnd != null));
                        const isLastSlice = index === sliceList.length - 1;

                        return (
                            <div key={slice.id || `${id}-${index}`} className="flex items-center gap-1.5 min-w-0">
                                <div className="min-w-0 flex-1">
                                    <SliceBlock
                                        dayId={id}
                                        index={index}
                                        dayOff={dayOff}
                                        isEditing={isEditing}
                                        isValid={valid}
                                        hasError={hasError}
                                        canDelete={sliceList.length > 1}
                                        startOptions={startOptions}
                                        endOptions={endOptions}
                                        control={control}
                                        getValues={getValues}
                                        options={options}
                                        onConfirm={() => confirmSlice(index)}
                                        onEdit={() => editSlice(index)}
                                        onDelete={() => handleRemoveSlice(index)}
                                    />
                                </div>
                                {isLastSlice && canAddSlice ? (
                                    <button
                                        type="button"
                                        className="flex items-center justify-center w-7 h-7 rounded border border-nt-blue text-nt-blue hover:bg-nt-blue hover:text-white shrink-0 transition"
                                        onClick={handleAddSlice}
                                        aria-label="Add time slice"
                                    >
                                        <FiPlus size={14} />
                                    </button>
                                ) : (
                                    <span className="w-7 h-7 shrink-0" aria-hidden="true" />
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="w-12 shrink-0 flex justify-center">
                    <Controller
                        name={`days.${id}.off`}
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <button
                                type="button"
                                role="switch"
                                aria-checked={Boolean(value)}
                                aria-label={`${shortName} day off`}
                                title="Day off"
                                onClick={() => onChange(!value)}
                                className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border transition-colors ${
                                    value ? "bg-nt-blue border-nt-blue" : "bg-nt-grey-light border-nt-grey-light"
                                }`}
                            >
                                <span
                                    className={`inline-block h-4 w-4 translate-y-0.5 rounded-full bg-white shadow transition-transform ${
                                        value ? "translate-x-4" : "translate-x-0.5"
                                    }`}
                                />
                            </button>
                        )}
                    />
                </div>
            </div>
            {dayErrorMessage ? (
                <p className="text-nt-red text-xs mt-1 pl-0 sm:pl-12">{dayErrorMessage}</p>
            ) : null}
        </div>
    );
};

export { SingleDaySlices };
